import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const sid = () => { let s = sessionStorage.getItem("tt_chat_sid"); if (!s) { s = crypto.randomUUID(); sessionStorage.setItem("tt_chat_sid", s); } return s; };
const GREETING = { role: "assistant", text: "Hi! I'm the studio assistant. Ask me about workshops, private parties, corporate events, or custom rugs — or ask to talk to a person." };

const Linkify = ({ text }) => text.split(/(\/[a-z-]+(?:\/[A-Za-z0-9:-]+)?)/g).map((part, i) =>
  /^\/[a-z-]+/.test(part) ? <Link key={i} to={part} className="underline font-semibold text-terracotta">{part}</Link> : <span key={i}>{part}</span>);

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([GREETING]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("ai");
  const [avail, setAvail] = useState(true);
  const [email, setEmail] = useState("");
  const [left, setLeft] = useState(false);
  const lastTs = useRef("");
  const endRef = useRef();
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  useEffect(() => {
    if (!open || mode !== "human") return;
    const poll = async () => {
      try {
        const { data } = await api.get(`/chat/${sid()}/updates`, { params: { since: lastTs.current } });
        setAvail(data.admin_available); if (data.mode) setMode(data.mode);
        if (data.messages?.length) { lastTs.current = data.messages[data.messages.length - 1].created_at; setMsgs((x) => [...x, ...data.messages.map((m) => ({ role: "admin", text: m.text }))]); }
      } catch { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 4000);
    return () => clearInterval(t);
  }, [open, mode]);

  const send = async (e) => {
    e?.preventDefault();
    const m = text.trim();
    if (!m || busy) return;
    setText(""); setBusy(true);
    setMsgs((x) => [...x, { role: "user", text: m }]);
    try {
      const { data } = await api.post("/chat", { session_id: sid(), message: m });
      if (data.mode) setMode(data.mode);
      if (typeof data.admin_available === "boolean") setAvail(data.admin_available);
      if (data.reply) setMsgs((x) => [...x, { role: "assistant", text: data.reply }]);
    } catch (err) {
      setMsgs((x) => [...x, { role: "assistant", text: err.response?.data?.detail || "Sorry, I couldn't respond. Please email info@thatstuft.com." }]);
    } finally { setBusy(false); }
  };
  const askHuman = () => { setText("I'd like to talk to a person"); setTimeout(() => document.querySelector("[data-testid=chat-send]")?.click(), 0); };
  const leaveMessage = async (e) => {
    e.preventDefault();
    if (!email) return;
    try { await api.post("/chat/leave-message", { session_id: sid(), email, message: "" }); setLeft(true); toast.success("Message sent — we'll email you back"); }
    catch { toast.error("Could not send. Please email info@thatstuft.com"); }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50" data-testid="chat-widget">
      {open && (
        <div className="mb-3 w-[calc(100vw-2.5rem)] sm:w-80 h-[28rem] bg-white rounded-3xl shadow-hover border-2 border-charcoal/10 flex flex-col overflow-hidden" data-testid="chat-panel">
          <div className="bg-charcoal text-cream px-4 py-3 flex items-center justify-between">
            <div><div className="font-serif text-lg leading-none">{mode === "human" ? "Studio Team" : "Studio Assistant"}</div>
              <div className="text-[11px] text-cream/60" data-testid="chat-status">{mode === "human" ? (avail ? "A person will reply here shortly" : "Owner unavailable — leave a message") : "Ask about booking & events"}</div></div>
            <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-close" className="p-1 rounded-full hover:bg-white/10"><X size={16}/></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 text-sm" data-testid="chat-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`max-w-[85%] rounded-2xl px-3 py-2 ${m.role === "user" ? "ml-auto bg-terracotta text-white" : m.role === "admin" ? "bg-forest/15 text-charcoal border border-forest/30" : "bg-cream-alt text-charcoal"}`} data-testid={`chat-msg-${m.role}`}>
                {m.role === "admin" && <div className="text-[10px] font-bold text-forest mb-0.5">Studio owner</div>}
                {m.role === "user" ? m.text : <Linkify text={m.text}/>}
              </div>
            ))}
            {busy && <div className="bg-cream-alt rounded-2xl px-3 py-2 w-14 text-charcoal-muted animate-pulse">…</div>}
            {mode === "human" && !avail && !left && (
              <form onSubmit={leaveMessage} className="bg-ochre/15 rounded-2xl p-3 space-y-2" data-testid="chat-leave-form">
                <div className="text-xs font-semibold">Leave your email and we'll reply to this conversation by email.</div>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="chat-leave-email" className="w-full rounded-full border border-cream-alt px-3 py-2 text-sm"/>
                <button type="submit" data-testid="chat-leave-submit" className="w-full rounded-full bg-charcoal text-cream text-xs font-bold py-2">Send my message</button>
              </form>
            )}
            {left && <div className="text-xs text-center text-charcoal-muted" data-testid="chat-left-confirm">Thanks — we'll get back to you by email.</div>}
            <div ref={endRef}/>
          </div>
          {mode === "ai" && <button type="button" onClick={askHuman} data-testid="chat-ask-human" className="mx-3 mb-1 text-[11px] text-charcoal-muted underline self-start">Talk to a person</button>}
          <form onSubmit={send} className="border-t border-cream-alt p-2 flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder={mode === "human" ? "Message the studio…" : "Type a question…"} maxLength={800} data-testid="chat-input" className="flex-1 rounded-full border border-cream-alt px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta"/>
            <button type="submit" disabled={busy || !text.trim()} aria-label="Send" data-testid="chat-send" className="w-9 h-9 rounded-full bg-terracotta text-white grid place-items-center disabled:opacity-40"><Send size={15}/></button>
          </form>
        </div>
      )}
      <button onClick={() => setOpen(!open)} aria-label="Chat with us" data-testid="chat-bubble" className="ml-auto flex w-14 h-14 rounded-full bg-terracotta text-white shadow-hover items-center justify-center hover:-translate-y-0.5 transition-transform">
        {open ? <X size={22}/> : <MessageCircle size={24}/>}
      </button>
    </div>
  );
}
